/**
 * Request Query Hooks
 *
 * TanStack Query hooks for request data, all routed through the API server.
 */

import type { Request } from '@cortex/database';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useFetchWithAuth } from './use-api';

/** Request with joined project data from by-number endpoint */
interface ApiRequestWithProject extends Request {
  project: {
    id: string;
    name: string;
    slug: string;
    organization_id: string;
  } | null;
  creator: {
    id: string;
    email: string;
    full_name: string | null;
  } | null;
}

export function useRequestByNumber(id: string) {
  const fetchWithAuth = useFetchWithAuth();
  const isNumeric = /^\d+$/.test(id);

  return useQuery({
    queryKey: ['request', id],
    queryFn: async () => {
      const path = isNumeric ? `/requests/by-number/${id}` : `/requests/${id}`;

      const res = await fetchWithAuth(path);
      if (!res.ok) {
        if (res.status === 404) throw new Error('Request not found');
        throw new Error('Failed to fetch request');
      }
      const json = await res.json();
      return json.data as ApiRequestWithProject;
    },
  });
}

interface CreateRequestParams {
  projectId: string;
  title: string;
  description?: string | undefined;
  type: string;
  priority: string;
}

export function useCreateRequest() {
  const fetchWithAuth = useFetchWithAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateRequestParams) => {
      const res = await fetchWithAuth(
        `/projects/${params.projectId}/requests`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: params.title,
            description: params.description ?? null,
            type: params.type,
            priority: params.priority,
          }),
        }
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error ?? 'Failed to create request');
      }
      const json = await res.json();
      return json.data as Request;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['project-requests'] });
    },
  });
}

interface UpdateRequestStatusParams {
  requestId: string;
  status: string;
}

export function useUpdateRequestStatus() {
  const fetchWithAuth = useFetchWithAuth();

  return useMutation({
    mutationFn: async (params: UpdateRequestStatusParams) => {
      const res = await fetchWithAuth(`/requests/${params.requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: params.status }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error ?? 'Failed to update request status');
      }
      const json = await res.json();
      return json.data as Request;
    },
  });
}
